import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Server, Cpu, MemoryStick, ArrowRight } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { Button, Card, CardBody } from '@vnetwork/ui/primitives';
import { useInstanceList, useInstanceSummaries, useInstanceDetails, type VmwareInstance, type VmwareInstanceSummary } from '@/api/vmware/instances';
import { vmTone, vmLabel, vmBucket } from './shared';
import cardStyles from '../Services.module.css';

/**
 * VMware console landing — instance status buckets + aggregate resource
 * consumption + recent-instance cards. Mirrors ComputeDashboard structure
 * but uses VMware hooks and omits OpenNebula-specific quotas.
 */
export function VmwareDashboard() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: instances = [], isLoading } = useInstanceList(serviceId);
  const base = `/services/compute/${serviceId}`;

  // The list endpoint returns rows WITHOUT metadata/power state. Power state comes from
  // the per-instance summary (summary.powerState), and CPU/RAM allocation from the
  // per-instance detail (metadata.cpu/memory). Batch-fetch both (React Query dedups).
  const ids = useMemo(() => instances.map((i) => i.id), [instances]);
  const summaries = useInstanceSummaries(serviceId, ids);
  const details = useInstanceDetails(serviceId, ids);

  const stats = useMemo(() => {
    const buckets = { running: 0, stopped: 0, error: 0, other: 0 };
    let cores = 0, memory = 0;
    for (const i of instances) {
      const ps = summaries[i.id]?.powerState ?? details[i.id]?.metadata?.power_state;
      buckets[vmBucket(ps)]++;
      cores += details[i.id]?.metadata?.cpu?.count ?? 0;
      const mib = details[i.id]?.metadata?.memory?.size_MiB;
      memory += mib != null ? Math.round(mib / 1024) : 0;
    }
    return { buckets, cores, memory };
  }, [instances, summaries, details]);

  const recent = [...instances].slice(0, 6);

  return (
    <Container size="xl">
      <PageHeader
        icon={<Server size={22} />}
        title="Overview"
        description="VMware virtual machines allocated to this service."
      />
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Section title="Status & usage" description="Power state and resource consumption." style={{ marginBottom: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--vn-space-3)', marginBottom: 'var(--vn-space-3)' }}>
            <StatusCard label="Running" value={stats.buckets.running} tone="success" />
            <StatusCard label="Powered off" value={stats.buckets.stopped} tone="muted" />
            <StatusCard label="Error" value={stats.buckets.error} tone="danger" />
            <StatusCard label="Other" value={stats.buckets.other} tone="warning" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--vn-space-3)' }}>
            <UsageCard icon={<Cpu size={16} />} label="vCPU" value={stats.cores} unit="" />
            <UsageCard icon={<MemoryStick size={16} />} label="Memory" value={stats.memory} unit="GiB" />
            <UsageCard icon={<Server size={16} />} label="Instances" value={instances.length} unit="" />
          </div>
        </Section>

        <Section
          title="Recent instances"
          description="Your most recently listed virtual machines."
          style={{ marginBottom: 0 }}
          actions={
            <Button size="sm" variant="secondary" endIcon={<ArrowRight size={15} />} onClick={() => navigate(`${base}/instances`)}>
              View all
            </Button>
          }
        >
          {isLoading ? null : recent.length === 0 ? (
            <Card>
              <CardBody>
                <EmptyState icon={<Server size={28} />} title="No instances yet" description="No virtual machines are provisioned for this service." />
              </CardBody>
            </Card>
          ) : (
            <div className={cardStyles.grid}>
              {recent.map((i) => (
                <RecentVmCard
                  key={i.id}
                  instance={i}
                  summary={summaries[i.id]}
                  detail={details[i.id]}
                  onOpen={() => navigate(`${base}/instances/${i.id}`)}
                />
              ))}
            </div>
          )}
        </Section>
      </div>
    </Container>
  );
}

function StatusCard({ label, value, tone }: { label: string; value: number; tone: 'success' | 'muted' | 'danger' | 'warning' }) {
  const color = tone === 'success' ? 'var(--vn-success-solid)' : tone === 'danger' ? 'var(--vn-danger-solid)' : tone === 'warning' ? 'var(--vn-warning-solid)' : 'var(--vn-fg-muted)';
  return (
    <Card flat>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', marginBottom: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} /> {label}
        </div>
        <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 700 }}>{value}</div>
      </CardBody>
    </Card>
  );
}

function UsageCard({ icon, label, value, unit }: { icon: React.ReactNode; label: string; value: number; unit: string }) {
  return (
    <Card flat>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--vn-fg-muted)', marginBottom: 6, fontSize: 'var(--vn-font-size-sm)' }}>
          {icon} {label}
        </div>
        <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 700 }}>
          {value}{unit ? <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400, fontSize: 'var(--vn-font-size-sm)', marginLeft: 4 }}>{unit}</span> : ''}
        </div>
      </CardBody>
    </Card>
  );
}

function RecentVmCard({ instance, summary, detail, onOpen }: {
  instance: VmwareInstance;
  summary?: VmwareInstanceSummary;
  detail?: VmwareInstance;
  onOpen: () => void;
}) {
  // CPU/RAM come from the per-instance detail (metadata); the list row has neither.
  const cpuCount = detail?.metadata?.cpu?.count;
  const memMiB = detail?.metadata?.memory?.size_MiB;
  const memGiB = memMiB != null ? Math.round(memMiB / 1024) : undefined;
  const sub = cpuCount != null ? `${cpuCount} vCPU · ${memGiB ?? '?'} GiB` : '';
  // Power state from summary.powerState (fallback to detail metadata.power_state).
  const powerState = summary?.powerState ?? detail?.metadata?.power_state;
  return (
    <div
      className={`${cardStyles.card} ${cardStyles.cardClickable}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
    >
      <div className={cardStyles.cardHead}>
        <span className={`${cardStyles.cardIcon} ${cardStyles.cardIconActive}`}>
          <Server size={19} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className={cardStyles.cardTitle}>{instance.name || instance.id}</div>
          {sub ? <div className={cardStyles.cardSub}>{sub}</div> : null}
        </div>
        <StatusPill tone={vmTone(powerState)}>{vmLabel(powerState)}</StatusPill>
      </div>
      <div className={cardStyles.cardFoot}>
        <span className={cardStyles.cardSub}>
          {/* IP not present on list rows — available in detail via summaryItems */}
          <code style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>—</code>
        </span>
        <span className={cardStyles.openHint}>Open <ArrowRight size={14} /></span>
      </div>
    </div>
  );
}
